/**
 * Gap between video tiles in px. The template binds it, so the layout math
 * below and the rendered gap cannot drift apart — a larger rendered gap would
 * wrap a row one tile early.
 */
export const GRID_GAP_PX = 8;

/** Standard webcam aspect ratio every tile is sized to; streams are contained. */
const TILE_ASPECT = 16 / 9;

/** Share of the container height the strip below a presentation stage may take. */
const STRIP_HEIGHT_SHARE = 0.2;

export type GridSize = 'small' | 'medium' | 'large';

export interface TileSize {
  width: number;
  height: number;
}

export interface VideoGridLayout {
  /** Width of the tile rows; the grid wraps after exactly the chosen column count. */
  gridWidth: number;
  tile: TileSize;
  /** Size of the first tile when it is a presentation stage, otherwise null. */
  stage: TileSize | null;
}

/** Coarse container size class; drives how many tiles are shown. */
export function gridSizeOf(width: number, height: number): GridSize {
  if (width < 500 || height < 300) {
    return 'small';
  }
  if ((width < 700 && height > 400) || (height < 600 && width > 500)) {
    return 'medium';
  }
  return 'large';
}

/**
 * Sizes `count` 16:9 tiles to fit a `width` × `height` container. In grid
 * mode every column count is tried and the one yielding the largest tiles
 * wins (ties go to fewer columns); in stage mode the first tile spans the
 * full width above a single strip of the others. Space the 16:9 tiles cannot
 * use is left as margin around the grid. Returns null when there is nothing
 * to lay out or the container is not measured yet.
 */
export function computeVideoGridLayout(options: {
  count: number;
  width: number;
  height: number;
  stage: boolean;
}): VideoGridLayout | null {
  const { count, width, height, stage } = options;
  if (count <= 0 || width <= 0 || height <= 0) {
    return null;
  }
  return stage && count > 1
    ? stageLayout(count - 1, width, height)
    : gridLayout(count, width, height);
}

function gridLayout(count: number, width: number, height: number): VideoGridLayout | null {
  let columns = 0;
  let tileWidth = 0;
  for (let candidate = 1; candidate <= count; candidate++) {
    const rows = Math.ceil(count / candidate);
    const candidateWidth = Math.floor(
      Math.min(
        (width - (candidate - 1) * GRID_GAP_PX) / candidate,
        ((height - (rows - 1) * GRID_GAP_PX) / rows) * TILE_ASPECT
      )
    );
    if (candidateWidth > tileWidth) {
      columns = candidate;
      tileWidth = candidateWidth;
    }
  }
  if (tileWidth <= 0) {
    return null;
  }
  return {
    gridWidth: columns * tileWidth + (columns - 1) * GRID_GAP_PX,
    tile: tileOfWidth(tileWidth),
    stage: null,
  };
}

function stageLayout(stripCount: number, width: number, height: number): VideoGridLayout | null {
  const tile = tileOfWidth(
    Math.floor(
      Math.min(
        (width - (stripCount - 1) * GRID_GAP_PX) / stripCount,
        height * STRIP_HEIGHT_SHARE * TILE_ASPECT
      )
    )
  );
  const stageWidth = Math.floor(width);
  const stageHeight = Math.floor(height - tile.height - GRID_GAP_PX);
  if (tile.width <= 0 || stageHeight <= 0) {
    return null;
  }
  return {
    gridWidth: stageWidth,
    tile,
    stage: { width: stageWidth, height: stageHeight },
  };
}

function tileOfWidth(width: number): TileSize {
  return { width, height: Math.floor(width / TILE_ASPECT) };
}
